import { useCallback, useEffect, useState } from "react";

import { calculateFootprintingProgress, getLocalKlimVar, setLocalKlimVar, useGetFootprintingCropFootprints, useGetFootprintingFarmFootprint } from "utils";

import { FOOTPRINTING_ONBOARDING } from "consts";

import { CarbonProgram } from "models";

export const useHasHiddenFootprintingOnboardingBanner = (farmId?: string, year?: number, carbonProgram?: CarbonProgram) => {
  const farmProgressQuery = useGetFootprintingFarmFootprint(farmId, year ?? new Date().getFullYear(), {
    enabled: carbonProgram?.platformFeatures?.footprinting?.enabled,
  });
  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, [year ?? new Date().getFullYear()], carbonProgram?.farmLevelFootprinting?.programCrops, {
    enabled: carbonProgram?.platformFeatures?.footprinting?.enabled,
  });

  const [hasHidden, setHasHidden] = useState<boolean>(() => {
    const storedValue = getLocalKlimVar(FOOTPRINTING_ONBOARDING.LOCALSTORAGE_KEY);
    if (!storedValue) return false;

    const { timestamp } = JSON.parse(storedValue);
    const isOlderThan30Days = Date.now() - timestamp > FOOTPRINTING_ONBOARDING.THIRTY_DAYS_MS;

    if (isOlderThan30Days && farmId && year) {
      // Checking progress after 30 days
      const { completionRate } = calculateFootprintingProgress(
        farmProgressQuery.data,
        cropProgressQueries.filter((q) => !!q.data).map((q) => q.data)
      );
      return completionRate > 0;
    }

    return true;
  });

  const hideBanner = useCallback(() => {
    const value = JSON.stringify({ timestamp: Date.now() });
    setLocalKlimVar(FOOTPRINTING_ONBOARDING.LOCALSTORAGE_KEY, value);
    setHasHidden(true);
  }, []);

  // localStorage changes in other tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === FOOTPRINTING_ONBOARDING.LOCALSTORAGE_KEY) {
        const storedValue = e.newValue ? JSON.parse(e.newValue) : null;
        if (!storedValue) {
          setHasHidden(false);
          return;
        }

        const isOlderThan30Days = Date.now() - storedValue.timestamp > FOOTPRINTING_ONBOARDING.THIRTY_DAYS_MS;
        if (isOlderThan30Days && farmId && year) {
          const { completionRate } = calculateFootprintingProgress(
            farmProgressQuery.data,
            cropProgressQueries.filter((q) => !!q.data).map((q) => q.data)
          );
          setHasHidden(completionRate > 0);
        } else {
          setHasHidden(true);
        }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [farmId, year, carbonProgram?.farmLevelFootprinting?.programCrops, farmProgressQuery.data, cropProgressQueries]);

  return { hasHidden, hideBanner };
};
